/**
 * LLMSwitcherController
 *
 * Header widget for switching the active AI model.
 * Pill button shows current model; dropdown lists all available models.
 * Persists selection in SettingsService (localStorage) and syncs to proxy.
 *
 * Sprint 59: LLM Model Switcher
 */

import { environment } from '../../config/environment';
import { SettingsService } from '../../utils/settings-service';

/** Shape returned by GET /llm/models */
interface ModelInfo {
    provider: string;
    model_id: string;
    display_name: string;
    supports_vision: boolean;
    supports_tools: boolean;
    supports_import: boolean;
    is_local: boolean;
}

export class LLMSwitcherController {
    private static instance: LLMSwitcherController | null = null;

    private btn: HTMLButtonElement | null = null;
    private dropdown: HTMLElement | null = null;
    private list: HTMLElement | null = null;
    private dot: HTMLElement | null = null;
    private label: HTMLElement | null = null;
    private models: Record<string, ModelInfo> = {};
    private isOpen = false;
    private isInitialized = false;
    private settings: SettingsService;
    private boundCloseHandler: (e: MouseEvent) => void;

    public static getInstance(): LLMSwitcherController {
        if (!LLMSwitcherController.instance) {
            LLMSwitcherController.instance = new LLMSwitcherController();
        }
        return LLMSwitcherController.instance;
    }

    private constructor() {
        this.settings = new SettingsService();
        this.boundCloseHandler = this.handleClickOutside.bind(this);
    }

    /**
     * Initialize — call after DOM ready and auth resolved.
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) return;

        this.btn = document.getElementById('llmSwitcherBtn') as HTMLButtonElement | null;
        this.dropdown = document.getElementById('llmSwitcherDropdown');
        this.list = document.getElementById('llmSwitcherList');
        this.dot = document.getElementById('llmSwitcherDot');
        this.label = document.getElementById('llmSwitcherLabel');

        if (!this.btn || !this.dropdown || !this.list) {
            console.warn('LLMSwitcher: DOM elements not found');
            return;
        }

        // Toggle dropdown on button click
        this.btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });

        // Load models from proxy
        await this.loadModels();

        // Sync proxy to persisted selection on startup
        const savedModel = this.settings.getAiModel();
        if (savedModel && this.models[savedModel]) {
            this.syncToProxy(savedModel);
        }

        // Listen for external changes (e.g. Settings page radios)
        this.settings.onChange('aiModel', (newVal) => {
            this.updateDisplay(newVal as string);
        });

        this.isInitialized = true;
        console.log('LLMSwitcher: initialized');
    }

    /**
     * Fetch model catalog from proxy and render the dropdown list.
     */
    private async loadModels(): Promise<void> {
        try {
            const resp = await fetch(`${environment.proxyUrl}/llm/models`);
            const json = await resp.json();
            if (json.Ok) {
                this.models = json.Ok as Record<string, ModelInfo>;
            }
        } catch (err) {
            console.warn('LLMSwitcher: failed to load models', err);
            // Provide hardcoded fallback so widget still works
            this.models = {
                'claude-sonnet-4-20250514': {
                    provider: 'anthropic', model_id: 'claude-sonnet-4-20250514',
                    display_name: 'Claude Sonnet 4', supports_vision: true,
                    supports_tools: true, supports_import: true, is_local: false,
                },
            };
        }
        this.renderList();
        this.updateDisplay(this.settings.getAiModel());
    }

    /**
     * Render the dropdown model list.
     */
    private renderList(): void {
        if (!this.list) return;

        const activeId = this.settings.getAiModel();
        const isMainnetEnv = environment.isMainnet;
        const entries = Object.values(this.models);

        this.list.innerHTML = entries.map(m => {
            const isActive = m.model_id === activeId;
            const disabled = isMainnetEnv && m.is_local;
            const badge = m.is_local
                ? '<span class="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">Local</span>'
                : '<span class="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Cloud</span>';
            const check = isActive
                ? '<svg class="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
                : '<span class="w-4 h-4 flex-shrink-0"></span>';
            const cursor = disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50';
            const title = disabled ? 'title="Ollama models unavailable on mainnet"' : '';

            return `<button
                class="w-full text-left px-3 py-2 flex items-center gap-2 ${cursor} transition-colors"
                data-model-id="${m.model_id}"
                ${disabled ? 'disabled' : ''}
                ${title}
            >
                ${check}
                <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-700 truncate">${m.display_name}</div>
                </div>
                ${badge}
            </button>`;
        }).join('');

        // Bind click handlers
        this.list.querySelectorAll<HTMLButtonElement>('button[data-model-id]').forEach(btn => {
            btn.addEventListener('click', () => {
                const modelId = btn.dataset.modelId;
                if (modelId && !btn.disabled) {
                    this.switchModel(modelId);
                }
            });
        });
    }

    /**
     * Switch to a different model.
     */
    public async switchModel(modelId: string): Promise<void> {
        if (!this.models[modelId]) return;

        // Update local settings immediately for responsive UI
        this.settings.setAiModel(modelId);
        this.updateDisplay(modelId);
        this.renderList();
        this.close();

        // Sync to proxy in background
        await this.syncToProxy(modelId);
    }

    /**
     * POST the model selection to the proxy.
     */
    private async syncToProxy(modelId: string): Promise<void> {
        try {
            await fetch(`${environment.proxyUrl}/llm/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ model_id: modelId }),
            });
        } catch (err) {
            console.warn('LLMSwitcher: failed to sync to proxy', err);
        }
    }

    /**
     * Update the pill display (dot colour + label text).
     */
    private updateDisplay(modelId: string): void {
        const info = this.models[modelId];
        if (!info) return;

        if (this.dot) {
            // Green = local, purple = cloud
            this.dot.className = `w-2 h-2 rounded-full ${info.is_local ? 'bg-green-500' : 'bg-purple-500'}`;
        }
        if (this.label) {
            this.label.textContent = info.display_name;
        }
    }

    private toggle(): void {
        this.isOpen ? this.close() : this.open();
    }

    private open(): void {
        if (!this.dropdown) return;
        this.dropdown.classList.remove('hidden');
        this.isOpen = true;
        // Defer to avoid the current click being caught
        setTimeout(() => document.addEventListener('click', this.boundCloseHandler), 0);
    }

    private close(): void {
        if (!this.dropdown) return;
        this.dropdown.classList.add('hidden');
        this.isOpen = false;
        document.removeEventListener('click', this.boundCloseHandler);
    }

    private handleClickOutside(e: MouseEvent): void {
        const container = document.getElementById('llmSwitcherContainer');
        if (container && !container.contains(e.target as Node)) {
            this.close();
        }
    }
}

/** Singleton export */
export const llmSwitcher = LLMSwitcherController.getInstance();
